/**
 * SuggestedForYou — network-style "Suggested for you" rail mixing trending
 * community topics + recommended courses. Data: GET /api/network/suggested-for-you
 *
 * Prop `compact` renders a slimmer sidebar-friendly version (used on member
 * profile pages). Otherwise a fuller card is rendered.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Sparkles, BookOpen, MessageSquare, ArrowRight } from 'lucide-react';

const SuggestedForYou = ({ compact = false }) => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState({ trending_topics: [], recommended_courses: [] });

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/network/suggested-for-you`)
      .then((r) => { if (alive) setData(r.data || { trending_topics: [], recommended_courses: [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [API]);

  const total = (data.trending_topics?.length || 0) + (data.recommended_courses?.length || 0);
  if (total === 0) return null;

  return (
    <aside className="pro-card p-4" data-testid="suggested-for-you">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <span className="font-heading font-extrabold text-sm">Suggested for you</span>
      </div>
      {data.trending_topics?.length > 0 && (
        <div className="space-y-1.5 mb-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Trending topics</div>
          <ul className="space-y-1">
            {data.trending_topics.slice(0, compact ? 3 : 5).map((t) => (
              <li key={t.id} data-testid={`suggest-topic-${t.id}`}>
                <Link
                  to={t.url}
                  className="flex items-start gap-2 rounded-lg hover:bg-muted p-1.5 -mx-1.5 transition-colors"
                >
                  <MessageSquare
                    className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                    style={{ color: t.category?.color || 'hsl(var(--primary))' }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-foreground line-clamp-2 leading-tight">
                      {t.title}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {t.category?.name || 'Community'} · {t.reply_count} repl{t.reply_count === 1 ? 'y' : 'ies'}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.recommended_courses?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Recommended lessons</div>
          <ul className="space-y-1">
            {data.recommended_courses.slice(0, compact ? 2 : 4).map((c) => (
              <li key={c.id} data-testid={`suggest-course-${c.id}`}>
                <Link
                  to={c.url}
                  className="flex items-start gap-2 rounded-lg hover:bg-muted p-1.5 -mx-1.5 transition-colors"
                >
                  {c.cover_image ? (
                    <img src={c.cover_image} alt="" className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-4 h-4" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-foreground line-clamp-2 leading-tight">
                      {c.title}
                    </div>
                    <div className="inline-flex items-center gap-0.5 text-[10px] text-primary font-bold mt-0.5">
                      Start lesson <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
};

export default SuggestedForYou;
